'use client';

import React from 'react';
import { CaretUp } from '@phosphor-icons/react';
import { Accordion as MuiAccordion, AccordionSummary, AccordionDetails, Box, Stack, Typography } from '@unity/cloud-ui';
import { useTheme } from '@unity/cloud-ui/styles';
import z from 'zod';
import { getComponentDocumentationUrl } from '@/utils/get-component-doc-url';

export const AccordionGroup = (props: z.input<typeof accordionGroupSchema>) => {
  const { children } = props;
  const [expanded, setExpanded] = React.useState<number | false>(false);

  const handleChange = (panelIndex: number) => (_: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panelIndex : false);
  };

  return (
    <Box sx={({ vars }) => ({ borderRadius: '4px', border: `1px solid ${vars.palette.divider}` })}>
      {React.Children.map(children, (child, index) => {
        return React.cloneElement(child, {
          expanded: expanded === index,
          onChange: handleChange(index),
          isAccordionGroupChild: true,
        });
      })}
    </Box>
  );
};

type AccordionProps = z.input<typeof accordionSchema> & {
  isAccordionGroupChild?: boolean;
};

export const Accordion = (props: AccordionProps) => {
  const { title, children, expanded, isAccordionGroupChild = false, ...rest } = props;
  const { vars, spacing } = useTheme();

  const accordionSummarySx = {
    borderTopLeftRadius: '3px',
    borderTopRightRadius: '3px',
    '.MuiAccordionSummary-content.Mui-expanded': {
      margin: spacing(1.5, 0),
    },
    '&.Mui-expanded': {
      minHeight: '48px !important',
      ...(!isAccordionGroupChild && {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      }),
    },
    ...(!isAccordionGroupChild && {
      borderBottomLeftRadius: '3px',
      borderBottomRightRadius: '3px',
    }),
    ...(isAccordionGroupChild && expanded
      ? { backgroundColor: vars.palette.primary.lighter }
      : {
          '&:hover': {
            backgroundColor: vars.palette.primary.lighter,
          },
        }),
  };

  return (
    <MuiAccordion
      expanded={expanded}
      {...rest}
      disableGutters
      sx={{
        ...(!isAccordionGroupChild && { border: `1px solid ${vars.palette.divider}`, borderRadius: '4px' }),
        '& > .MuiAccordion-heading .MuiAccordionSummary-expandIconWrapper': {
          transform: 'rotate(-180deg)',
        },
      }}
    >
      <AccordionSummary expandIcon={<CaretUp />} sx={accordionSummarySx}>
        <Typography variant="h5" component="div">
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 2, borderTop: `1px solid ${vars.palette.divider}` }}>
        <Stack gap={2}>{children}</Stack>
      </AccordionDetails>
    </MuiAccordion>
  );
};

const accordionDocumentationUrl = getComponentDocumentationUrl('accordions');
const accordionGroupDocumentationUrl = getComponentDocumentationUrl('accordion-groups');

const descriptions = {
  accordion: {
    schema: `Display an accordion with a title and content. \n\n\n\n**Example**\n\`\`\`mdx\n<Accordion title="Title">\n  The content of the accordion\n</Accordion>\n\`\`\`\n\n${accordionDocumentationUrl}`,
    title: 'The title of the accordion.',
    children: 'The content of the accordion. Can be anything.',
    expanded: 'Whether the accordion is expanded by default.',
  },
  accordionGroup: {
    schema: `Display a group of accordions. \n\n\n\n**Example**\n\`\`\`mdx\n<AccordionGroup>\n  <Accordion title="Title">\n    The content of the accordion\n  </Accordion>\n</AccordionGroup>\n\`\`\`\n\n${accordionGroupDocumentationUrl}`,
    children: `The list of accordions.\n\n\n\n**Example**\n\`\`\`mdx\n<AccordionGroup>\n  /***\n  *Here is where the children property will be rendered.\n  */\n</AccordionGroup>\n\`\`\`\n\n${accordionGroupDocumentationUrl}`,
  },
};

export const accordionSchema = z
  .object({
    title: z.string().describe(descriptions.accordion.title),
    children: z.any().describe(descriptions.accordion.children),
    expanded: z.boolean().describe(descriptions.accordion.expanded).optional(),
  })
  .describe(descriptions.accordion.schema);

export const accordionGroupSchema = z
  .object({
    children: z.any().describe(descriptions.accordionGroup.children),
  })
  .describe(descriptions.accordionGroup.schema);
